import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Avatar,
  Box,
  ButtonBase,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
  fetchFollowList,
  type FollowListKind,
} from '../../services/profileMedia';
import type { ProfileUser } from '../../services/mediaContracts';
import { renderRichText } from './richText';

interface FollowListDialogProps {
  open: boolean;
  handle: string;
  kind: FollowListKind;
  onClose: () => void;
  onSelectUser: (screenName: string) => void;
}

const formatCount = (n?: number): string => {
  if (n == null) return '';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
};

const FollowListDialog: React.FC<FollowListDialogProps> = ({
  open,
  handle,
  kind,
  onClose,
  onSelectUser,
}) => {
  const [users, setUsers] = useState<ProfileUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);

  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const cursorRef = useRef<string | null>(null);
  const hasMoreRef = useRef(false);
  const loadingMoreRef = useRef(false);
  const requestIdRef = useRef(0);

  const title = kind === 'followers' ? 'Followers' : 'Following';

  const loadInitial = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError(null);
    setUsers([]);
    setHasMore(false);
    cursorRef.current = null;
    hasMoreRef.current = false;

    try {
      const page = await fetchFollowList(handle, kind, null, 40);
      if (requestId !== requestIdRef.current) return;
      setUsers(page.users);
      cursorRef.current = page.nextCursor;
      const more = Boolean(page.nextCursor);
      setHasMore(more);
      hasMoreRef.current = more;
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      setError(err instanceof Error ? err.message : 'Failed to load list');
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [handle, kind]);

  const loadMore = useCallback(async () => {
    if (
      !hasMoreRef.current ||
      !cursorRef.current ||
      loadingMoreRef.current ||
      loading
    ) {
      return;
    }

    loadingMoreRef.current = true;
    setLoadingMore(true);
    const requestId = requestIdRef.current;

    try {
      const page = await fetchFollowList(handle, kind, cursorRef.current, 40);
      if (requestId !== requestIdRef.current) return;

      setUsers((prev) => {
        const seen = new Set(prev.map((u) => u.screen_name.toLowerCase()));
        const next = page.users.filter(
          (u) => !seen.has(u.screen_name.toLowerCase())
        );
        return next.length ? [...prev, ...next] : prev;
      });

      cursorRef.current = page.nextCursor;
      const more = Boolean(page.nextCursor);
      setHasMore(more);
      hasMoreRef.current = more;
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      setError(err instanceof Error ? err.message : 'Failed to load more');
      hasMoreRef.current = false;
      setHasMore(false);
    } finally {
      loadingMoreRef.current = false;
      if (requestId === requestIdRef.current) {
        setLoadingMore(false);
      }
    }
  }, [handle, kind, loading]);

  useEffect(() => {
    if (!open) {
      requestIdRef.current += 1;
      return;
    }
    void loadInitial();
  }, [open, loadInitial]);

  useEffect(() => {
    if (!open || !hasMore || loading) return;
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          void loadMore();
        }
      },
      { root: sentinel.parentElement, rootMargin: '240px 0px' }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [open, hasMore, loading, users.length, loadMore]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          backgroundColor: '#16181C',
          backgroundImage: 'none',
          border: '1px solid #2F3336',
          borderRadius: 2,
          maxHeight: { xs: '88vh', sm: '80vh' },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          pr: 1,
          py: 1.5,
          borderBottom: '1px solid #2F3336',
          color: '#E7E9EA',
          fontWeight: 700,
          fontSize: '1.0625rem',
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {title}
          <Typography
            component="span"
            variant="body2"
            sx={{ display: 'block', color: '#71767B', fontWeight: 400, mt: 0.25 }}
          >
            @{handle.replace(/^@/, '')}
          </Typography>
        </Box>
        <IconButton onClick={onClose} aria-label="Close" sx={{ color: '#E7E9EA' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers={false}
        sx={{
          p: 0,
          overflowY: 'auto',
          overscrollBehavior: 'contain',
        }}
      >
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={32} sx={{ color: '#1D9BF0' }} />
          </Box>
        )}

        {!loading && error && (
          <Box sx={{ px: 2.5, py: 4, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: '#E7E9EA', mb: 1.5 }}>
              {error}
            </Typography>
            <ButtonBase
              onClick={() => void loadInitial()}
              sx={{
                color: '#1D9BF0',
                fontWeight: 600,
                fontSize: '0.875rem',
                fontFamily: 'inherit',
              }}
            >
              Try again
            </ButtonBase>
          </Box>
        )}

        {!loading && !error && users.length === 0 && (
          <Typography
            variant="body2"
            sx={{ color: '#71767B', textAlign: 'center', py: 6, px: 2 }}
          >
            No {kind} to show.
          </Typography>
        )}

        {!loading &&
          users.map((user) => {
            const avatar = user.avatar_url
              ? user.avatar_url.replace('_normal.', '_200x200.')
              : undefined;
            return (
              <ButtonBase
                key={`${user.id}-${user.screen_name}`}
                onClick={() => onSelectUser(user.screen_name)}
                sx={{
                  display: 'flex',
                  width: '100%',
                  alignItems: 'flex-start',
                  gap: 1.5,
                  px: 2,
                  py: 1.5,
                  textAlign: 'left',
                  borderBottom: '1px solid #2F3336',
                  '&:hover': { backgroundColor: 'rgba(231, 233, 234, 0.04)' },
                }}
              >
                <Avatar
                  src={avatar}
                  alt={user.name}
                  imgProps={{ referrerPolicy: 'no-referrer' }}
                  sx={{ width: 48, height: 48, bgcolor: '#2F3336', flexShrink: 0 }}
                >
                  {(user.name || user.screen_name).charAt(0).toUpperCase()}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography
                      sx={{
                        color: '#E7E9EA',
                        fontWeight: 700,
                        fontSize: '0.9375rem',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {user.name || user.screen_name}
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{ color: '#71767B', lineHeight: 1.3 }}
                    noWrap
                  >
                    @{user.screen_name}
                  </Typography>
                  {user.description && (
                    <Typography
                      variant="body2"
                      component="div"
                      sx={{
                        color: '#E7E9EA',
                        mt: 0.5,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        lineHeight: 1.4,
                        wordBreak: 'break-word',
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {renderRichText(user.description, {
                        onMentionClick: (screenName) => onSelectUser(screenName),
                      })}
                    </Typography>
                  )}
                  {user.followers != null && (
                    <Typography
                      variant="caption"
                      sx={{ color: '#71767B', display: 'block', mt: 0.5 }}
                    >
                      <Box component="span" sx={{ color: '#E7E9EA', fontWeight: 600 }}>
                        {formatCount(user.followers)}
                      </Box>{' '}
                      Followers
                    </Typography>
                  )}
                </Box>
              </ButtonBase>
            );
          })}

        <div ref={sentinelRef} />

        {loadingMore && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={22} sx={{ color: '#1D9BF0' }} />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default FollowListDialog;
